import { useState } from "react";
import { REFERENCE_SITES, SUB_ITEMS, allSites, siteAssignedSubs } from "./checklistData";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Globe, ExternalLink, KeyRound, ChevronDown, Plus, Trash2 } from "lucide-react";

const GROUPS = SUB_ITEMS.reduce((acc, si) => { (acc[si.group] ||= []).push(si); return acc; }, {});

export default function ResourcesTab({ briefing, setBriefing }) {
  const creds = briefing.siteCreds || {};
  const sites = allSites(briefing);
  const [nn, setNn] = useState("");
  const [nu, setNu] = useState("");
  const [ng, setNg] = useState("");

  const setCred = (id, key, v) =>
    setBriefing((b) => ({ ...b, siteCreds: { ...(b.siteCreds || {}), [id]: { ...((b.siteCreds || {})[id] || {}), [key]: v } } }));
  const toggleSub = (siteId, subId) =>
    setBriefing((b) => {
      const cur = siteAssignedSubs(b, siteId);
      const next = cur.includes(subId) ? cur.filter((x) => x !== subId) : [...cur, subId];
      return { ...b, siteSubs: { ...(b.siteSubs || {}), [siteId]: next } };
    });
  const addSite = () => {
    const name = nn.trim();
    let url = nu.trim();
    if (!name || !url) return;
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    const id = `custom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setBriefing((b) => ({ ...b, customSites: [...(b.customSites || []), { id, name, url, usage: ng.trim(), custom: true }] }));
    setNn(""); setNu(""); setNg("");
  };
  const removeSite = (id) =>
    setBriefing((b) => {
      const siteSubs = { ...(b.siteSubs || {}) }; delete siteSubs[id];
      const siteCreds = { ...(b.siteCreds || {}) }; delete siteCreds[id];
      return { ...b, customSites: (b.customSites || []).filter((s) => s.id !== id), siteSubs, siteCreds };
    });

  return (
    <div className="space-y-4">
      <div className="glass rounded-2xl p-6">
        <h3 className="mb-1 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">
          <Globe className="h-4 w-4" /> Sites web de référence
        </h3>
        <p className="mb-4 text-xs text-slate-500">
          Cochez les sous-items auxquels chaque site correspond (WINTEM, METAR/TAF, SUP AIP, AZBA, Carte de nav, VAC…) ; les liens seront proposés par défaut sous ces sous-items dans la check-list et la présentation. Identifiant / mot de passe optionnels (stockés dans ce briefing).
        </p>
        <div className="space-y-3">
          {sites.map((s) => {
            const subs = siteAssignedSubs(briefing, s.id);
            return (
              <div key={s.id} data-testid={`resource-${s.id}`} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`resource-link-${s.id}`}
                    className="flex items-center gap-1.5 font-display font-semibold text-cyan-300 hover:text-cyan-200">
                    {s.name} <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  {s.usage && <span className="text-xs text-slate-400">— {s.usage}</span>}
                  {s.custom && (
                    <button onClick={() => removeSite(s.id)} data-testid={`resource-remove-${s.id}`}
                      className="ml-auto rounded p-1.5 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
                <div className="mt-0.5 break-all font-mono-data text-[11px] text-slate-500">{s.url}</div>

                <div className="mt-3">
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Sous-items associés</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid={`resource-subs-${s.id}`}
                        className="mt-1 flex w-full items-center justify-between rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-200 hover:border-cyan-500/40">
                        <span className="truncate">{subs.length ? `${subs.length} sous-item(s)` : "Aucun sous-item"}</span>
                        <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="max-h-80 w-80 overflow-auto border-slate-700 bg-slate-900 p-2">
                      {Object.entries(GROUPS).map(([g, items]) => (
                        <div key={g} className="mb-1">
                          <div className="px-1 py-1 text-[10px] uppercase tracking-wider text-cyan-500/70">{g}</div>
                          {items.map((si) => (
                            <label key={si.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1.5 text-sm text-slate-200 hover:bg-slate-800">
                              <Checkbox checked={subs.includes(si.id)} onCheckedChange={() => toggleSub(s.id, si.id)}
                                data-testid={`resource-sub-${s.id}-${si.id}`} />
                              <span className="truncate">{si.label}</span>
                            </label>
                          ))}
                        </div>
                      ))}
                    </PopoverContent>
                  </Popover>
                  {subs.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {subs.map((id) => {
                        const si = SUB_ITEMS.find((x) => x.id === id);
                        return <span key={id} className="rounded bg-slate-800 px-2 py-0.5 text-[11px] text-slate-300">{si ? si.label : id}</span>;
                      })}
                    </div>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500"><KeyRound className="h-3 w-3" /> Identifiant</Label>
                    <Input value={creds[s.id]?.login || ""} onChange={(e) => setCred(s.id, "login", e.target.value)}
                      placeholder="(optionnel)" data-testid={`cred-login-${s.id}`}
                      className="mt-1 h-9 border-slate-700 bg-slate-900/60 text-sm text-slate-100" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Mot de passe</Label>
                    <Input value={creds[s.id]?.password || ""} onChange={(e) => setCred(s.id, "password", e.target.value)}
                      placeholder="(optionnel)" data-testid={`cred-pass-${s.id}`}
                      className="mt-1 h-9 border-slate-700 bg-slate-900/60 text-sm text-slate-100" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 rounded-xl border border-dashed border-slate-700 bg-slate-900/30 p-4">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-cyan-400">
            <Plus className="h-3.5 w-3.5" /> Ajouter un site de référence
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Input value={nn} onChange={(e) => setNn(e.target.value)} placeholder="Nom du site" data-testid="new-site-name"
              className="h-9 border-slate-700 bg-slate-900/60 text-sm text-slate-100" />
            <Input value={nu} onChange={(e) => setNu(e.target.value)} placeholder="URL (https://…)" data-testid="new-site-url"
              className="h-9 border-slate-700 bg-slate-900/60 text-sm text-slate-100" />
            <Input value={ng} onChange={(e) => setNg(e.target.value)} placeholder="Utilité (optionnel)" data-testid="new-site-usage"
              className="h-9 border-slate-700 bg-slate-900/60 text-sm text-slate-100" />
          </div>
          <Button onClick={addSite} data-testid="new-site-add"
            className="mt-2 bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
            <Plus className="mr-1 h-3.5 w-3.5" /> Ajouter le site
          </Button>
        </div>
      </div>
    </div>
  );
}
